import { useMemo } from 'react';

import { Card, Form } from '@arco-design/web-react';

import { ServicePreviewSection } from '../../../capabilities/service/ServicePreviewSection';
import { getDeployPluginHeadless } from '../../../core/registry';
import type { DeployResourcePreviewProps } from '../../../core/types';
import { DescriptorDescriptions } from '../../../platform/display-descriptors';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import { isSkuCapableProfile } from '../../../platform/pipeline-kernel';
import { resolvePreviewModuleKeys } from '../../../platform/scene-layout';
import { buildPreviewEnabledModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import type { EbmSpec } from '../headless/schema';
import { EBM_MODULES } from '../modules';

const PREVIEW_WATCH_FIELDS = [
  'name',
  'alias',
  'description',
  'namespace',
  'workspace',
  'datacenter',
  'runStrategy',
  'systemDisk',
  'resource',
  'resourceConfiguration',
  'optionMeta',
  'skuResourceModules',
] as const;

const PREVIEW_EMPTY = '未配置';
const ALL_MODULE_KEYS = EBM_MODULES.map((mod) => mod.key);

function groupDescriptorsByModule(items: DisplayDescriptor[]) {
  const map = new Map<string, DisplayDescriptor[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return map;
}

function renderModuleContent(modKey: string, items: DisplayDescriptor[], services: EbmSpec['resource']['network']['service']) {
  const column = modKey === 'baseInfo' ? 2 : 1;

  if (modKey === 'service') {
    return <ServicePreviewSection services={services} />;
  }

  const displayItems = items.length ? items : [{ id: `${modKey}-empty`, group: modKey, label: '-', value: PREVIEW_EMPTY }];

  return <DescriptorDescriptions items={displayItems} column={column} />;
}

export function EbmResourcePreview({ form, spec, descriptors, ctx, modules: moduleFilter }: DeployResourcePreviewProps) {
  const plugin = getDeployPluginHeadless('ElasticBareMetal');

  Form.useWatch([...PREVIEW_WATCH_FIELDS], form);

  const formValues = form?.getFieldsValue() ?? {};
  const skuResourceModules = formValues.skuResourceModules as Record<string, boolean> | undefined;
  const scopeModuleKeys = moduleFilter?.length ? moduleFilter : ALL_MODULE_KEYS;

  const enabledModuleKeys = useMemo(() => {
    if (moduleFilter?.length) return moduleFilter;
    if (!form) return ALL_MODULE_KEYS;
    return resolvePreviewModuleKeys(form, 'ElasticBareMetal', ALL_MODULE_KEYS);
  }, [form, moduleFilter, skuResourceModules]);

  const previewEnabledModules = useMemo(
    () => buildPreviewEnabledModules(scopeModuleKeys, skuResourceModules),
    [scopeModuleKeys, skuResourceModules],
  );

  const previewCtx = {
    ...ctx,
    namespace: ctx?.namespace ?? (formValues.namespace as string | undefined),
    cluster: ctx?.cluster ?? (formValues.datacenter as string | undefined),
    sku: { enabledModules: previewEnabledModules },
  };

  const resolvedSpec = (spec ?? plugin.pipeline.schema.parse(plugin.pipeline.fromForm(formValues, previewCtx))) as EbmSpec;
  const profile = plugin.pipeline.projectionProfile;
  const flatItems =
    descriptors ?? (isSkuCapableProfile(profile) ? profile.projections.buildDisplayDescriptors(resolvedSpec, previewCtx) : []);

  const grouped = groupDescriptorsByModule(flatItems);
  const services = resolvedSpec.resource?.network?.service;
  const visibleModules = EBM_MODULES.filter((mod) => enabledModuleKeys.includes(mod.key));

  if (!visibleModules.length) {
    return null;
  }

  return (
    <>
      {visibleModules.map((mod) => {
        const items = grouped.get(mod.key) ?? [];

        return (
          <Card key={mod.key} bordered={false} title={mod.title} style={{ marginBottom: 12 }}>
            {renderModuleContent(mod.key, items, services)}
          </Card>
        );
      })}
    </>
  );
}
